import React, {useState, useEffect} from "react";
import {
    Box, Typography, Paper, Button, CircularProgress, Chip, IconButton,
    Dialog, DialogTitle, DialogContent, DialogActions, TextField,
    FormControl, InputLabel, Select, MenuItem, Switch, FormControlLabel,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Alert, Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import CloseIcon from "@mui/icons-material/Close";
import {showSnackbar} from "../../components/SnackBar/SnackBar";
import {accountingApi} from "../../services/api/accounting";
import {getLandlordId} from "../../services/api/client";

const ACCOUNT_TYPES = ["ASSET", "LIABILITY", "EQUITY", "REVENUE", "EXPENSE"];

const TYPE_COLORS = {
    ASSET: "#1565c0",
    LIABILITY: "#c62828",
    EQUITY: "#2e7d32",
    REVENUE: "#00838f",
    EXPENSE: "#e65100",
};

const TYPE_BG = {
    ASSET: "#e3f2fd",
    LIABILITY: "#ffebee",
    EQUITY: "#e8f5e9",
    REVENUE: "#e0f7fa",
    EXPENSE: "#fff3e0",
};

export default function ChartOfAccounts() {
    const [loading, setLoading] = useState(true);
    const [accounts, setAccounts] = useState([]);
    const [showInactive, setShowInactive] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const [editOpen, setEditOpen] = useState(false);
    const [saving, setSaving] = useState(false);

    // Add form
    const [newCode, setNewCode] = useState("");
    const [newName, setNewName] = useState("");
    const [newType, setNewType] = useState("EXPENSE");
    const [newParentId, setNewParentId] = useState("");

    // Edit form
    const [editAccount, setEditAccount] = useState(null);
    const [editName, setEditName] = useState("");
    const [editActive, setEditActive] = useState(true);

    useEffect(() => {
        fetchAccounts();
    }, []);

    const fetchAccounts = async () => {
        setLoading(true);
        try {
            const landlordId = getLandlordId();
            const response = await accountingApi.getAccounts(landlordId);
            if (response.ok && response.data?.accounts) {
                setAccounts(response.data.accounts);
            }
        } catch (err) {
            console.error("Failed to fetch accounts:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async () => {
        if (!newCode || !newName || !newType) {
            showSnackbar({title: "Error!", message: "Code, name, and type are required.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            return;
        }
        setSaving(true);
        try {
            const body = {
                landlord_id: getLandlordId(),
                code: newCode,
                name: newName,
                account_type: newType,
            };
            if (newParentId) body.parent_id = parseInt(newParentId);

            const response = await accountingApi.addAccount(body);
            if (response.ok) {
                showSnackbar({title: "Success!", message: "Account created.", type: "success", position: {vertical: "bottom", horizontal: "right"}});
                setAddOpen(false);
                setNewCode(""); setNewName(""); setNewType("EXPENSE"); setNewParentId("");
                fetchAccounts();
            } else {
                showSnackbar({title: "Error!", message: response.data?.Error || "Failed to create account.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Something went wrong.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = async () => {
        if (!editAccount) return;
        setSaving(true);
        try {
            const body = {
                landlord_id: getLandlordId(),
                account_id: editAccount.id,
                name: editName,
                is_active: editActive,
            };
            const response = await accountingApi.updateAccount(body);
            if (response.ok) {
                showSnackbar({title: "Success!", message: "Account updated.", type: "success", position: {vertical: "bottom", horizontal: "right"}});
                setEditOpen(false);
                fetchAccounts();
            } else {
                showSnackbar({title: "Error!", message: response.data?.Error || "Failed to update account.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
            }
        } catch (err) {
            showSnackbar({title: "Error!", message: "Something went wrong.", type: "error", position: {vertical: "bottom", horizontal: "right"}});
        } finally {
            setSaving(false);
        }
    };

    const openEdit = (account) => {
        setEditAccount(account);
        setEditName(account.name);
        setEditActive(account.is_active);
        setEditOpen(true);
    };

    // Group accounts by type
    const groupedAccounts = ACCOUNT_TYPES.reduce((acc, type) => {
        acc[type] = accounts.filter(a => a.account_type === type && (showInactive || a.is_active));
        return acc;
    }, {});

    // Parent account options for add form
    const parentOptions = accounts.filter(a => a.account_type === newType && a.is_system);

    // Auto-suggest code
    const suggestCode = () => {
        if (!newParentId) return;
        const parent = accounts.find(a => a.id === parseInt(newParentId));
        if (!parent) return;
        const siblings = accounts.filter(a => a.parent_id === parent.id);
        const nextNum = parseInt(parent.code) + siblings.length + 1;
        setNewCode(String(nextNum));
    };

    if (loading) {
        return <Box sx={{display: "flex", justifyContent: "center", py: 10}}><CircularProgress/></Box>;
    }

    return (
        <Box sx={{py: 3}}>
            <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3}}>
                <Typography variant="h5" fontWeight={600} sx={{color: "#2C5283"}}>
                    Chart of Accounts
                </Typography>
                <Box sx={{display: "flex", gap: 2, alignItems: "center"}}>
                    <FormControlLabel
                        control={<Switch checked={showInactive} onChange={(e) => setShowInactive(e.target.checked)} size="small"/>}
                        label={<Typography variant="body2">Show inactive</Typography>}
                    />
                    <Button variant="contained" startIcon={<AddIcon/>} onClick={() => setAddOpen(true)}
                        sx={{background: "linear-gradient(45deg, #2C5283, #1E3A63)", textTransform: "none"}}>
                        Add Account
                    </Button>
                </Box>
            </Box>

            {ACCOUNT_TYPES.map(type => {
                const typeAccounts = groupedAccounts[type];
                if (typeAccounts.length === 0) return null;
                return (
                    <Paper key={type} sx={{mb: 3, borderRadius: 2, overflow: "hidden"}}>
                        <Box sx={{px: 2, py: 1.5, bgcolor: TYPE_BG[type], borderBottom: `2px solid ${TYPE_COLORS[type]}`}}>
                            <Typography variant="subtitle1" fontWeight={600} sx={{color: TYPE_COLORS[type]}}>
                                {type}
                            </Typography>
                        </Box>
                        <TableContainer>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{fontWeight: 600, width: 100}}>Code</TableCell>
                                        <TableCell sx={{fontWeight: 600}}>Name</TableCell>
                                        <TableCell sx={{fontWeight: 600, width: 120}}>Status</TableCell>
                                        <TableCell sx={{fontWeight: 600, width: 80}} align="center">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {typeAccounts.map(account => (
                                        <TableRow key={account.id} sx={{opacity: account.is_active ? 1 : 0.5}}>
                                            <TableCell>
                                                <Typography variant="body2" fontWeight={500} sx={{fontFamily: "monospace"}}>
                                                    {account.code}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                    {account.name}
                                                    {account.is_system && (
                                                        <Tooltip title="System account - cannot be modified">
                                                            <LockIcon sx={{fontSize: 14, color: "#999"}}/>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={account.is_active ? "Active" : "Inactive"}
                                                    size="small"
                                                    color={account.is_active ? "success" : "default"}
                                                    variant="outlined"
                                                    sx={{fontSize: "11px"}}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                {!account.is_system && (
                                                    <IconButton size="small" onClick={() => openEdit(account)}>
                                                        <EditIcon fontSize="small"/>
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Paper>
                );
            })}

            {/* Add Account Dialog */}
            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    Add Custom Account
                    <IconButton onClick={() => setAddOpen(false)} size="small"><CloseIcon/></IconButton>
                </DialogTitle>
                <DialogContent sx={{pt: 2}}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
                        <FormControl fullWidth>
                            <InputLabel>Account Type</InputLabel>
                            <Select value={newType} label="Account Type" onChange={(e) => { setNewType(e.target.value); setNewParentId(""); }}>
                                {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <FormControl fullWidth>
                            <InputLabel>Parent Account (Optional)</InputLabel>
                            <Select value={newParentId} label="Parent Account (Optional)" onChange={(e) => { setNewParentId(e.target.value); }}>
                                <MenuItem value="">None</MenuItem>
                                {parentOptions.map(a => <MenuItem key={a.id} value={a.id}>{a.code} - {a.name}</MenuItem>)}
                            </Select>
                        </FormControl>
                        <Box sx={{display: "flex", gap: 2}}>
                            <TextField label="Account Code" value={newCode} onChange={(e) => setNewCode(e.target.value)} sx={{width: 150}}
                                helperText={newParentId ? <span style={{cursor: "pointer", color: "#2C5283"}} onClick={suggestCode}>Auto-suggest</span> : ""}/>
                            <TextField label="Account Name" value={newName} onChange={(e) => setNewName(e.target.value)} fullWidth/>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{p: 2}}>
                    <Button onClick={() => setAddOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleAdd} variant="contained" disabled={saving}
                        sx={{background: "linear-gradient(45deg, #2C5283, #1E3A63)"}}>
                        {saving ? "Creating..." : "Create Account"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Edit Account Dialog */}
            <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
                    Edit Account
                    <IconButton onClick={() => setEditOpen(false)} size="small"><CloseIcon/></IconButton>
                </DialogTitle>
                <DialogContent sx={{pt: 2}}>
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2, mt: 1}}>
                        <TextField label="Account Name" value={editName} onChange={(e) => setEditName(e.target.value)} fullWidth/>
                        <FormControlLabel
                            control={<Switch checked={editActive} onChange={(e) => setEditActive(e.target.checked)}/>}
                            label="Active"
                        />
                        {!editActive && (
                            <Alert severity="warning">Deactivated accounts won't appear in new transactions.</Alert>
                        )}
                    </Box>
                </DialogContent>
                <DialogActions sx={{p: 2}}>
                    <Button onClick={() => setEditOpen(false)} color="inherit">Cancel</Button>
                    <Button onClick={handleEdit} variant="contained" disabled={saving}
                        sx={{background: "linear-gradient(45deg, #2C5283, #1E3A63)"}}>
                        {saving ? "Saving..." : "Save Changes"}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}
